"use client";

import { PortableText } from "next-sanity";
import { motion } from "framer-motion";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";
import { GROQImage } from "@/sanity/lib/definitions";
import { PortableTextBlock } from "sanity";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import { useCarouselApi } from "@/hooks/useCarouselApi";
import { cn } from "@/lib/utils";

const ease = [0.25, 0.1, 0.25, 1] as const;

interface QuoteItem {
  _key?: string;
  text?: PortableTextBlock[];
  name?: string;
  role?: string;
  photo?: GROQImage;
}

interface IFeaturedQuoteSectionData {
  featuredQuotes?: QuoteItem[];
  featuredQuoteText?: PortableTextBlock[];
  featuredQuoteName?: string;
  featuredQuoteRole?: string;
  featuredQuotePhoto?: GROQImage;
}

function QuoteIcon() {
  return (
    <svg
      viewBox="0 0 46 35"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[4.375rem] w-[5.75rem] shrink-0 rotate-180"
      aria-hidden="true"
    >
      <path
        d="M19.1341 0V14.3714C19.1341 25.4625 11.983 32.9797 1.91667 35L0.00958333 30.8175C4.67092 29.0345 7.66667 23.7436 7.66667 19.4444H0V0H19.1341ZM46 0V14.3714C46 25.4625 38.8163 32.9817 28.75 35L26.841 30.8175C31.5042 29.0345 34.5 23.7436 34.5 19.4444H26.8659V0H46Z"
        fill="#228E71"
      />
    </svg>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12.75 10.625"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

function QuoteSlide({ item }: { item: QuoteItem }) {
  if (!item.text) return null;
  return (
    <div className="flex flex-col items-center gap-[3rem]">
      <div className="quoteText richtext w-[min(141.5rem,100%)] text-center prose-p:mb-0 prose-p:font-geist prose-p:text-[4rem] prose-p:font-normal prose-p:leading-[1.35] prose-p:tracking-[-0.2875rem] prose-p:text-black lg:prose-p:text-[5.75rem]">
        <PortableText value={item.text} components={richTextComponent} />
      </div>

      <div className="h-[4.875rem] w-px bg-black/10" />

      <div className="flex items-center gap-[2.5rem]">
        {item.photo && (
          <div className="relative h-[7.625rem] w-[7.625rem] shrink-0 overflow-hidden rounded-[0.25rem]">
            <GroqImage
              src={item.photo}
              alt={item.name ?? ""}
              className="!absolute !inset-0 !h-full !w-full !object-cover"
            />
          </div>
        )}
        <div className="flex flex-col gap-[0.5rem]">
          {item.name && (
            <p className="mb-0 font-geist text-[3.5rem] font-medium leading-[1.12] tracking-[-0.14rem] text-black">
              {item.name}
            </p>
          )}
          {item.role && (
            <p className="mb-0 font-geist-mono text-[1.625rem] font-semibold uppercase leading-[1.33] text-[#003828]">
              {item.role}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SingleQuote({ data }: { data: IFeaturedQuoteSectionData }) {
  if (!data.featuredQuoteText) return null;
  return (
    <QuoteSlide
      item={{
        text: data.featuredQuoteText,
        name: data.featuredQuoteName,
        role: data.featuredQuoteRole,
        photo: data.featuredQuotePhoto,
      }}
    />
  );
}

function QuotesCarousel({ quotes }: { quotes: QuoteItem[] }) {
  const { setApi, handlePrev, handleNext, current, count, isFirst, isLast } =
    useCarouselApi();

  const progress = count > 1 ? (current - 1) / (count - 1) : 1;

  return (
    <div className="flex flex-col items-center gap-[4rem] w-full">
      <Carousel
        setApi={setApi}
        opts={{ align: "start", loop: false }}
        className="w-full overflow-hidden"
      >
        <CarouselContent>
          {quotes.map((item, i) => (
            <CarouselItem key={item._key ?? i} className="pl-0 basis-full">
              <QuoteSlide item={item} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {count > 1 && (
        <div className="flex w-[min(52rem,calc(100%-4rem))] items-center gap-[2rem]">
          <button
            type="button"
            onClick={handlePrev}
            disabled={isFirst}
            aria-label="Previous"
            className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full border border-black/20 bg-white transition-opacity disabled:opacity-30 hover:opacity-70"
          >
            <ArrowIcon className="h-[2.125rem] w-[2.125rem] rotate-180 text-black" />
          </button>

          <div className="h-[0.5rem] flex-1 overflow-hidden rounded-[3rem] bg-black/10">
            <motion.div
              className="h-full rounded-full bg-black"
              animate={{ width: `${progress * 100}%` }}
              transition={{ duration: 0.3, ease }}
            />
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={isLast}
            aria-label="Next"
            className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black transition-opacity disabled:opacity-30 hover:opacity-80"
          >
            <ArrowIcon className="h-[2.125rem] w-[2.125rem] text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function FeaturedQuoteSection({
  data,
  className,
}: {
  data: IFeaturedQuoteSectionData;
  className?: string;
}) {
  const hasCarousel = data.featuredQuotes && data.featuredQuotes.length > 0;
  const hasFallback = !!data.featuredQuoteText;

  if (!hasCarousel && !hasFallback) return null;

  return (
    <section
      className={cn(
        "w-full bg-white py-[10rem] lg:py-[16rem] flex flex-col items-center",
        className,
      )}
    >
      <div className="mx-auto flex w-[min(140rem,calc(100%-4rem))] flex-col items-center gap-[3rem]">
        <QuoteIcon />

        {hasCarousel ? (
          <QuotesCarousel quotes={data.featuredQuotes!} />
        ) : (
          <SingleQuote data={data} />
        )}
      </div>
    </section>
  );
}
